// src/pages/Laboratorio/Acompanhamento/CaseHistoricoTable.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import ArrowDownIcon from "../../../assets/icons/arrow-down.svg";
import { useTheme } from "../../../context/ThemeContext";

type SortConfig = { index: number | null; direction: "asc" | "desc" };
type Cell = string | number | React.ReactNode;

export type CaseHistoricoRow = {
  id: string; // id da linha na UI (ex: hist_40 ou draft...)
  cells: Cell[];
  locked?: boolean;
  __kind?: "draft" | "real";

  // ✅ meta pra exclusão server-side
  __hist_id?: string | number | null; // id real do lab_reparo_status_historico
  __reparo_id?: string | null;
};

type DropHint = { index: number; where: "before" | "after" } | null;

export type CaseHistoricoTableProps = {
  headers: string[];
  rows: CaseHistoricoRow[];

  columnWidths?: string[];
  dense?: boolean;
  softBorders?: boolean;
  enableZebra?: boolean;
  zebraColors?: { odd: string; even: string };
  hoverHighlight?: boolean;

  enableReorder?: boolean;
  enableDelete?: boolean;
  enableLock?: boolean;

  onRowsChange?: (next: CaseHistoricoRow[]) => void;

  draftRowId?: string;
  draftCells?: Cell[] | null;

  renderRowActions?: (row: CaseHistoricoRow) => React.ReactNode | null;

  // ✅ callback pra deletar no backend
  onDeleteRowServer?: (row: CaseHistoricoRow) => Promise<void>;
};

function clamp(n: number, a: number, b: number) {
  return Math.max(a, Math.min(b, n));
}

function toText(v: any): string {
  if (v === null || v === undefined) return "";
  if (typeof v === "string" || typeof v === "number") return String(v);
  return "";
}

const CaseHistoricoTable: React.FC<CaseHistoricoTableProps> = ({
  headers,
  rows,
  columnWidths = [],
  dense = false,
  softBorders = false,
  enableZebra = true,
  zebraColors = { odd: "#f6f8fb", even: "#fbfcfe" },
  hoverHighlight = true,
  enableReorder = true,
  enableDelete = true,
  enableLock = true,
  onRowsChange,
  draftRowId,
  draftCells,
  renderRowActions,
  onDeleteRowServer,
}) => {
  const [filters, setFilters] = useState<string[]>(
    Array(headers.length).fill("")
  );
  const [sort, setSort] = useState<SortConfig>({
    index: null,
    direction: "asc",
  });
  const [hoverRow, setHoverRow] = useState<number | null>(null);
  const [fontSize, setFontSize] = useState("14px");

  const { theme } = useTheme();
  const headerPrimary = theme?.primary ?? "#124280";
  const optionHover = theme?.table_option_hover ?? "#D3E1F0";
  const borderColor = softBorders ? "#e5e7eb" : "black";

  const realRowsRef = useRef<CaseHistoricoRow[]>(rows ?? []);
  useEffect(() => {
    realRowsRef.current = rows ?? [];
  }, [rows]);

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dropHint, setDropHint] = useState<DropHint>(null);
  const [ghost, setGhost] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
    cells: Cell[];
    bg: string;
  } | null>(null);

  const draggingIdRef = useRef<string | null>(null);
  const dropHintRef = useRef<DropHint>(null);
  const visibleRef = useRef<CaseHistoricoRow[]>([]);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const clampedSize = clamp(w / 120, 12, 18);
      setFontSize(clampedSize + "px");
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const baseRows = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  useEffect(() => {
    setDraggingId(null);
    setDropHint(null);
    setGhost(null);
    draggingIdRef.current = null;
    dropHintRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, [rows, headers.length]);

  const parseMaybeNumber = (v: string): number | null => {
    const n = Number(
      v
        .replace(/[^\d,.-]/g, "")
        .replace(/\./g, "")
        .replace(/,/g, ".")
    );
    return Number.isFinite(n) ? n : null;
  };

  const getComparable = (cell: any) => {
    if (typeof cell === "number") return { kind: "num" as const, val: cell };
    if (typeof cell === "string") {
      const maybe = parseMaybeNumber(cell);
      if (maybe !== null) return { kind: "num" as const, val: maybe };
      return { kind: "str" as const, val: cell };
    }
    return { kind: "str" as const, val: "" };
  };

  const anyFilterOn = useMemo(
    () => filters.some((f) => (f ?? "").trim()),
    [filters]
  );
  const isSortOn = sort.index != null;
  const dragEnabled = enableReorder && !anyFilterOn && !isSortOn;

  const { visible } = useMemo(() => {
    const filtered: CaseHistoricoRow[] = [];

    baseRows.forEach((it) => {
      const passes = it.cells.every((cell, i) => {
        const f = (filters[i] ?? "").toLowerCase();
        if (!f) return true;
        const v = toText(cell).toLowerCase();
        return v.includes(f);
      });
      if (passes) filtered.push(it);
    });

    if (sort.index != null) {
      const i = sort.index;
      const m = sort.direction === "asc" ? 1 : -1;
      filtered.sort((a, b) => {
        const A = getComparable(a.cells[i]);
        const B = getComparable(b.cells[i]);
        if (A.kind === "num" && B.kind === "num") {
          if (A.val < B.val) return -1 * m;
          if (A.val > B.val) return 1 * m;
          return 0;
        }
        return (
          String(A.val).localeCompare(String(B.val), "pt-BR", {
            sensitivity: "base",
            numeric: true,
          }) * m
        );
      });
    }

    return { visible: filtered };
  }, [baseRows, filters, sort]);

  useEffect(() => {
    visibleRef.current = visible;
  }, [visible]);

  const headerHeight = dense ? "36px" : "40px";
  const filterHeight = dense ? "30px" : "35px";
  const cellHeight = dense ? "32px" : "35px";
  const cellPadding = dense ? "0 4px" : "0 5px";

  const rowBaseBg = (i: number) =>
    enableZebra
      ? i % 2 === 0
        ? zebraColors.odd
        : zebraColors.even
      : "#ffffff";

  const computeDropHint = (
    viewIndex: number,
    clientY: number,
    rowEl: HTMLElement
  ): DropHint => {
    const rect = rowEl.getBoundingClientRect();
    const mid = rect.top + rect.height / 2;
    const where: "before" | "after" = clientY < mid ? "before" : "after";
    return { index: viewIndex, where };
  };

  const isDraftRow = (row: CaseHistoricoRow) =>
    !!draftRowId && row.id === draftRowId;

  const reorderById = (dragId: string, hint: DropHint) => {
    if (!hint) return;
    if (!dragEnabled) return;
    if (!onRowsChange) return;

    const currentReal = realRowsRef.current ?? [];
    const srcIndex = currentReal.findIndex((r) => r.id === dragId);
    if (srcIndex < 0) return;

    if (isDraftRow(currentReal[srcIndex])) return;
    if (currentReal[srcIndex]?.locked) return;

    const currentVisible = visibleRef.current;
    const targetViewIndex = hint.index;
    if (targetViewIndex < 0 || targetViewIndex >= currentVisible.length) return;

    const targetId = currentVisible[targetViewIndex].id;
    const targetRealIndex = currentReal.findIndex((r) => r.id === targetId);
    if (targetRealIndex < 0) return;

    let destIndex = targetRealIndex + (hint.where === "after" ? 1 : 0);
    if (srcIndex < destIndex) destIndex -= 1;
    if (destIndex === srcIndex) return;

    const next = [...currentReal];
    const [moved] = next.splice(srcIndex, 1);
    next.splice(destIndex, 0, moved);
    onRowsChange(next);
  };

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const id = draggingIdRef.current;
      if (!id) return;
      setGhost((g) => (g ? { ...g, x: e.clientX + 12, y: e.clientY + 12 } : g));
    };

    const onUp = () => {
      const id = draggingIdRef.current;
      if (!id) return;

      reorderById(id, dropHintRef.current);

      draggingIdRef.current = null;
      dropHintRef.current = null;
      setDraggingId(null);
      setDropHint(null);
      setGhost(null);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dragEnabled, onRowsChange]);

  const placeholderRowStyle: React.CSSProperties = { height: 10 };
  const placeholderCellStyle: React.CSSProperties = {
    padding: 0,
    borderLeft: `1px solid ${borderColor}`,
    borderRight: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    borderTop: `1px solid ${borderColor}`,
    background: "rgba(18, 66, 128, 0.10)",
  };
  const renderPlaceholder = (key: string) => (
    <tr key={key} style={placeholderRowStyle}>
      <td colSpan={headers.length + 1} style={placeholderCellStyle}>
        <div style={{ height: 10, transition: "height 140ms ease" }} />
      </td>
    </tr>
  );

  const toggleLock = (rowId: string) => {
    if (!enableLock) return;
    if (!onRowsChange) return;
    const current = realRowsRef.current ?? [];
    const next = current.map((r) =>
      r.id === rowId ? { ...r, locked: !r.locked } : r
    );
    onRowsChange(next);
  };

  const deleteRow = async (row: CaseHistoricoRow) => {
    if (!enableDelete) return;
    if (!onRowsChange) return;
    if (row.locked) return;
    if (isDraftRow(row)) return;

    const preview = String(
      (row.cells?.[0] as any)?.props?.label ?? row.cells?.[0] ?? ""
    );

    const ok = window.confirm(
      onDeleteRowServer
        ? `Excluir este status do histórico (irá remover do sistema)?\n\n${preview}`
        : `Excluir esta linha do histórico (apenas nesta tela)?\n\n${preview}`
    );
    if (!ok) return;

    // 1) backend (se existir)
    if (onDeleteRowServer) {
      await onDeleteRowServer(row);
    }

    // 2) remove da UI (página pode recarregar depois; ok)
    const current = realRowsRef.current ?? [];
    const next = current.filter((r) => r.id !== row.id);
    onRowsChange(next);
  };

  const actionsWidth = enableLock ? "170px" : "76px";

  return (
    <div style={{ width: "100%", overflowX: "auto", position: "relative" }}>
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          fontFamily: "Inter, sans-serif",
        }}
      >
        <thead>
          <tr>
            {headers.map((header, index) => (
              <th
                key={index}
                onClick={() =>
                  setSort((prev) =>
                    prev.index === index
                      ? {
                          index,
                          direction: prev.direction === "asc" ? "desc" : "asc",
                        }
                      : { index, direction: "asc" }
                  )
                }
                style={{
                  width: columnWidths[index] || "auto",
                  backgroundColor: headerPrimary,
                  color: "#ffffff",
                  fontWeight: 300,
                  fontSize,
                  height: headerHeight,
                  padding: "0 8px",
                  border: `1px solid ${borderColor}`,
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                  textAlign: "center",
                  cursor: "pointer",
                  userSelect: "none",
                }}
              >
                <div
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <span>{header}</span>
                  {sort.index === index && (
                    <img
                      src={ArrowDownIcon}
                      alt="ordenar"
                      style={{
                        width: 18,
                        height: 18,
                        marginLeft: 6,
                        transform:
                          sort.direction === "asc" ? "rotate(180deg)" : "none",
                        transition: "transform 120ms ease",
                      }}
                    />
                  )}
                </div>
              </th>
            ))}

            <th
              style={{
                width: actionsWidth,
                backgroundColor: headerPrimary,
                color: "#ffffff",
                fontWeight: 300,
                fontSize,
                height: headerHeight,
                padding: "0 8px",
                border: `1px solid ${borderColor}`,
                position: "sticky",
                top: 0,
                zIndex: 2,
                textAlign: "center",
                userSelect: "none",
              }}
            >
              Ações
            </th>
          </tr>
        </thead>

        <tbody>
          <tr>
            {headers.map((_, index) => (
              <td
                key={`filter-${index}`}
                style={{
                  width: columnWidths[index] || "auto",
                  height: filterHeight,
                  border: `1px solid ${borderColor}`,
                  padding: "0 8px",
                  background: "#f8fafc",
                }}
              >
                <input
                  type="text"
                  value={filters[index]}
                  onChange={(e) => {
                    const nf = [...filters];
                    nf[index] = e.target.value;
                    setFilters(nf);

                    setDraggingId(null);
                    setDropHint(null);
                    setGhost(null);
                    draggingIdRef.current = null;
                    dropHintRef.current = null;
                  }}
                  style={{
                    width: "100%",
                    height: dense ? "24px" : "28px",
                    border: "none",
                    outline: "none",
                    fontSize,
                    textAlign: "center",
                    textTransform: "uppercase",
                    background: "transparent",
                  }}
                />
              </td>
            ))}
            <td
              style={{
                width: actionsWidth,
                height: filterHeight,
                border: `1px solid ${borderColor}`,
                background: "#f8fafc",
              }}
            />
          </tr>

          {visible.length ? (
            visible.map((row, viewIndex) => {
              const base = rowBaseBg(viewIndex);
              const bg =
                hoverHighlight && hoverRow === viewIndex ? optionHover : base;

              const draftRow = isDraftRow(row);
              const isDraggingThis = draggingId === row.id;

              const showBefore =
                dropHint &&
                dropHint.index === viewIndex &&
                dropHint.where === "before" &&
                draggingId &&
                draggingId !== row.id;

              const showAfter =
                dropHint &&
                dropHint.index === viewIndex &&
                dropHint.where === "after" &&
                draggingId &&
                draggingId !== row.id;

              const canDrag = dragEnabled && !row.locked && !draftRow;
              const rowOpacity = isDraggingThis ? 0.35 : 1;

              const onMouseDownRow = (
                e: React.MouseEvent<HTMLTableRowElement>
              ) => {
                if (!canDrag) return;
                if (e.button !== 0) return;

                document.body.style.userSelect = "none";
                document.body.style.cursor = "grabbing";

                setDraggingId(row.id);
                draggingIdRef.current = row.id;

                const rect = (
                  e.currentTarget as HTMLElement
                ).getBoundingClientRect();
                setGhost({
                  x: e.clientX + 12,
                  y: e.clientY + 12,
                  width: rect.width,
                  height: rect.height,
                  cells: row.cells,
                  bg,
                });

                setDropHint(null);
                dropHintRef.current = null;
              };

              const onMouseEnterRow = () =>
                hoverHighlight && setHoverRow(viewIndex);
              const onMouseLeaveRow = () => hoverHighlight && setHoverRow(null);

              const onMouseMoveOverThisRow = (
                e: React.MouseEvent<HTMLTableRowElement>
              ) => {
                if (!dragEnabled) return;
                const id = draggingIdRef.current;
                if (!id) return;

                if (id === row.id) {
                  setDropHint(null);
                  dropHintRef.current = null;
                  return;
                }

                const hint = computeDropHint(
                  viewIndex,
                  e.clientY,
                  e.currentTarget
                );
                setDropHint(hint);
                dropHintRef.current = hint;
              };

              const customActions = renderRowActions?.(row);

              return (
                <React.Fragment key={row.id}>
                  {showBefore && renderPlaceholder(`ph-before-${row.id}`)}

                  <tr
                    onMouseDown={onMouseDownRow}
                    onMouseEnter={(e) => {
                      onMouseEnterRow();
                      onMouseMoveOverThisRow(e);
                    }}
                    onMouseLeave={onMouseLeaveRow}
                    onMouseMoveCapture={onMouseMoveOverThisRow}
                    style={{
                      cursor: canDrag ? "grab" : "default",
                      opacity: rowOpacity,
                      transition: "opacity 120ms ease",
                      background: bg,
                    }}
                    title={
                      draftRow
                        ? "Novo status (edição)"
                        : row.locked
                        ? "Item bloqueado"
                        : !dragEnabled
                        ? "Limpe filtros e desative ordenação para reordenar"
                        : "Segure e arraste para reordenar"
                    }
                  >
                    {(draftRow &&
                    draftCells &&
                    draftCells.length === headers.length
                      ? draftCells
                      : row.cells
                    ).map((cell, j) => (
                      <td
                        key={`cell-${row.id}-${j}`}
                        style={{
                          width: columnWidths[j] || "auto",
                          height: cellHeight,
                          border: `1px solid ${borderColor}`,
                          fontSize,
                          fontWeight: 300,
                          textAlign: "center",
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          padding: cellPadding,
                          background: bg,
                        }}
                      >
                        {cell}
                      </td>
                    ))}

                    <td
                      style={{
                        width: actionsWidth,
                        height: cellHeight,
                        border: `1px solid ${borderColor}`,
                        textAlign: "center",
                        padding: "0 6px",
                        background: bg,
                      }}
                    >
                      {customActions ? (
                        customActions
                      ) : draftRow ? null : (
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            justifyContent: "center",
                          }}
                        >
                          {enableLock && (
                            <button
                              type="button"
                              onClick={() => toggleLock(row.id)}
                              style={{
                                height: 28,
                                minWidth: 86,
                                borderRadius: 0,
                                border: `1px solid ${borderColor}`,
                                background: "#fff",
                                cursor: "pointer",
                                fontSize: 12,
                              }}
                            >
                              {row.locked ? "Desbloquear" : "Bloquear"}
                            </button>
                          )}

                          {enableDelete && (
                            <button
                              type="button"
                              disabled={!!row.locked}
                              onClick={() => deleteRow(row)}
                              style={{
                                height: 28,
                                minWidth: 64,
                                borderRadius: 0,
                                border: `1px solid ${
                                  row.locked ? "#d1d5db" : borderColor
                                }`,
                                background: row.locked ? "#f3f4f6" : "#fff",
                                cursor: row.locked ? "not-allowed" : "pointer",
                                fontSize: 12,
                                opacity: row.locked ? 0.6 : 1,
                              }}
                            >
                              Excluir
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>

                  {showAfter && renderPlaceholder(`ph-after-${row.id}`)}
                </React.Fragment>
              );
            })
          ) : (
            <tr>
              <td
                colSpan={headers.length + 1}
                style={{
                  padding: 16,
                  textAlign: "center",
                  color: "#6B7280",
                  fontSize,
                  border: `1px solid ${borderColor}`,
                  background: "#fff",
                }}
              >
                Nenhum registro encontrado com os filtros atuais.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {ghost && (
        <div
          style={{
            position: "fixed",
            left: ghost.x,
            top: ghost.y,
            width: ghost.width,
            pointerEvents: "none",
            zIndex: 9999,
            background: ghost.bg,
            border: `1px solid ${borderColor}`,
            boxShadow: "0 10px 25px rgba(0,0,0,0.18)",
            opacity: 0.98,
          }}
        >
          <table
            style={{
              borderCollapse: "collapse",
              width: "100%",
              fontFamily: "Inter, sans-serif",
            }}
          >
            <tbody>
              <tr>
                {ghost.cells.map((c, idx) => (
                  <td
                    key={`ghost-${idx}`}
                    style={{
                      width: columnWidths[idx] || "auto",
                      height: ghost.height,
                      border: `1px solid ${borderColor}`,
                      fontSize,
                      fontWeight: 300,
                      textAlign: "center",
                      padding: cellPadding,
                      background: ghost.bg,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {c}
                  </td>
                ))}
                <td
                  style={{
                    width: actionsWidth,
                    height: ghost.height,
                    border: `1px solid ${borderColor}`,
                    background: ghost.bg,
                  }}
                />
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CaseHistoricoTable;
